<template>
  <div>
    <b-row>
      <b-col xl="2" lg="3" cols="12" class="report-sidebar p-3">
        <b-overlay style="height: calc(100vh - 128px);" :show="isBusy" rounded="sm">
          <b-form-group label="Datasets">
            <b-form-select v-if="datasets.length" size="sm" v-model="dataset" :options="datasets" @change="listTables"/>
          </b-form-group>
          <b-row>
            <b-col>
              <b-form-group v-if="tables.length" label="Tables">
                <b-form-select size="sm" v-model="table" :options="tables" text-field="tableId" value-field="tableId"
                               placeholder="Select table"
                               @change="selectTable"/>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-if="selectedTable" class="mb-3">
            <b-col>
              <bq-template-handler :selected-table="selectedTable" :current-query-model="queryModel"
                                   :templates="templates" :current-template="currentTemplate"
                                   @saveTemplate="saveTemplate" @selectedTemplate="loadTemplate"
                                   @editTemplate="editTemplate"
                                   @removeTemplate="removeTemplate"/>
            </b-col>
          </b-row>

          <b-row v-if="selectedTable">
            <b-col>
              <bq-filter-fields v-model="selectedColumns" :columns="selectedTable.fields"/>
            </b-col>
          </b-row>
        </b-overlay>
      </b-col>
      <b-col xl="10" lg="9" cols="12" class="report-body">
        <div v-if="selectedTable">
          <b-row class="pt-4 px-3 mb-0">
            <b-col>
              <span class="d-inline-block table-title">{{ selectedTable.reference.tableId }}</span>
              <small class="text-muted float-right">Total row count: {{ formatNumber(selectedTable.numRows) }}</small>
              <small class="text-muted float-right mr-3">Last modified: {{
                  formatDatetime(selectedTable.lastModified)
                }}</small>
            </b-col>
          </b-row>
          <hr style="margin-bottom: 0.5rem">
          <b-row>
            <b-col>
              <bq-filter-handler ref="filterHandler" v-model="queryModel" :datasource-id="datasourceId"
                                 :selected-table="selectedTable"
                                 :user-selected-columns="selectedColumns" :brands="brands"/>
            </b-col>
          </b-row>
          <hr style="margin-top: 0;">
          <b-overlay :show="isBusy" rounded="sm">
            <b-row v-if="selectedColumns.length" class="mb-3">
              <b-col>
                <b-icon-info-circle ref="icon-query-info" variant="primary" shift-v="-6" class="ml-3 float-right"/>
                <b-tooltip :target="() => $refs['icon-query-info']" triggers="hover">
                  <p>Query will group dimensions and SUM measures.</p>
                  <p><strong>Preview</strong> limited to 1,000 rows. <strong>Export</strong> limited to 500,000 rows.
                  </p>
                  <p></p>
                  <p>Use <strong>Export Raw Data</strong> to get data without aggregation.</p>
                </b-tooltip>
                <b-button-toolbar class="float-right">
                  <b-button-group class="mr-2">
                    <b-button variant="outline-primary" size="sm" @click="exportData(false)">
                      Export Raw Data
                    </b-button>
                  </b-button-group>
                  <b-button class="mr-1" variant="outline-primary" size="sm" @click="exportData(true)">
                    Export Data
                  </b-button>
                  <b-button variant="primary" size="sm" @click="queryData(true)">
                    Preview Data
                  </b-button>
                </b-button-toolbar>

              </b-col>
            </b-row>
            <b-row>
              <b-col>
                <div v-if="result.length">
                  <data-viewer :fields="fields" :items="result" :is-busy="isBusy"></data-viewer>
                </div>
              </b-col>
            </b-row>
          </b-overlay>
        </div>
      </b-col>
    </b-row>
    <span></span>
  </div>
</template>

<script>
import axios from "axios";
import dayjs from "dayjs";
import _ from 'lodash';
import DataViewer from "../../components/datagrabber/bqGrabber/BqDataViewer.vue";
import BqFilterHandler from "../../components/datagrabber/bqGrabber/BqFilterHandler.vue";
import BqFilterFields from "../../components/datagrabber/bqGrabber/BqFilterFields.vue";
import BqTemplateHandler from "../../components/datagrabber/bqGrabber/BqTemplateHandler.vue";
import dataGrabberServices from "@/DataGrabberServices";

export default {
  name: "BigQueryGrabber",
  components: {BqTemplateHandler, BqFilterFields, BqFilterHandler, DataViewer},
  async mounted() {
    await this.listDatasets();
    await this.listTables();
    this.getBrands();
  },
  data() {
    return {
      datasourceId: 1,
      datasets: [],
      dataset: null,
      tables: [],
      table: null,
      brands: [],

      queryModel: {},

      templates: [],

      selectedTable: null,
      selectedColumns: [],
      isBusy: false,
      result: [],
      fields: [],

      currentTemplate: null,
    }
  },
  computed: {
    selectedTableId() {
      if (this.selectedTable && this.selectedTable.reference) {
        return this.selectedTable.reference.tableId;
      }
      return '';
    }
  },
  methods: {
    loadFieldsForViewer() {
      let fields = [...this.selectedTable.fields]
      const columns = this.queryModel.columns;
      let sortedField = [];
      _.each(columns, (c) => {
        let f = _.find(fields, {name: c.name});
        sortedField.push({...f, ...{columnType: c.columnType, aggregateFunction: c.aggregateFunction}});
      });
      const tableFields = [];

      _.forEach(sortedField, (field, index) => {
        let fieldClass = field.columnType === 'dimension' ? 'smartgrabber-dimension' : 'smartgrabber-measure';
        let fieldLabel = `${field.name}${field.columnType === 'measure' ? `(${field.aggregateFunction || 'sum'})` : ''}`;
        let tableField = {
          key: index.toString(),
          label: fieldLabel,
          sortable: true,
          sortByFormatted: true,
          filterByFormatted: true,
          class: fieldClass,
        };
        if (field.aggregateFunction && field.aggregateFunction.toLowerCase().includes('count')) {
          tableField.formatter = 'FormatInteger';
        }
        switch (field.type.toLowerCase()) {
          case 'integer':
            tableField.formatter = 'FormatInteger';
            break;
          case 'float':
            tableField.formatter = 'FormatDecimal';
            break;
          case 'numeric':
            tableField.formatter = 'FormatDecimal';
            break;
          case 'date':
            tableField.formatter = 'FormatDate';
            break;
          default:
            break;
        }
        tableField.sortByFormatted = true;
        tableFields.push(tableField);
      });

      this.fields = tableFields;
    },

    formatNumber(number) {
      return new Intl.NumberFormat().format(number);
    },
    formatDatetime(datetime) {
      return dayjs(datetime).format('YYYY-MM-DD HH:mm');
    },
    async selectTable() {
      await this.getTable(this.table);
      await this.getTemplates();
    },

    async getBrands() {
      // const response = await axios.get('api/bigQuery/brands');
      const response = dataGrabberServices.getBrands();
      this.brands = response.data;
    },
    async listDatasets() {
      // temporary hardcode to BIBO
      this.dataset = 'BIBO'
      this.datasets = [{value: 'BIBO', text: 'BIBO'}]
      // const resp = await dataGrabberServices.getDatasets(this.datasourceId);
      // this.datasets = _.map(resp.data, v => {
      //   return {value: v.key, text: v.value}
      // });
      // this.dataset = this.datasets[0].value;
    },

    async listTables() {
      const resp = await dataGrabberServices.getTables(this.datasourceId, this.dataset);
      // const response = await axios.get(`api/bigQuery/datasets/${this.dataset}/tables`);
      this.tables = resp.data;
    },

    async getTable(tableId) {
      this.isBusy = true;
      this.selectedTable = null;
      this.result = [];
      // const response = await axios.get(`api/bigQuery/datasets/${this.dataset}/table`,
      //     {params: {tableId: tableId}});
      const response = await dataGrabberServices.getTable(this.datasourceId, this.dataset, tableId);
      this.selectedTable = response.data;
      this.currentTemplate = null;
      this.isBusy = false;
    },

    async queryData(isAggregated = false) {
      if (!(await this.validateQueryModel())) {
        return;
      }
      this.isBusy = true;
      const postData = {...this.queryModel, ...{isAggregated: isAggregated}};
      try {
        // const response = await axios.post(`api/bigQuery/datasets/${this.dataset}/query`, postData);
        const response = await dataGrabberServices.getQueryResult(this.datasourceId, this.dataset, this.table, postData);
        this.loadFieldsForViewer();
        this.result = response.data;
        if (!response.data.length) {
          await this.$bvModal.msgBoxOk("No data.", {centered: true, size: 'sm', 'hide-header': true});
        }
      } finally {
        this.isBusy = false;
      }
    },

    async exportData(isAggregated = false) {
      if (!(await this.validateQueryModel())) {
        return;
      }
      this.isBusy = true;
      const postData = {...this.queryModel, ...{isAggregated: isAggregated}};
      // let rowCount = await axios.post(`api/bigQuery/datasets/${this.dataset}/query-row-count`, postData);
      // let formattedRowCount = rowCount.data.toLocaleString();
      let proceed = await this.$bvModal.msgBoxConfirm(
          `Currently the limit of rows that can be exported is 500,000. Do you want to proceed?`, {
            centered: true,
            okTitle: 'Proceed',
            buttonSize: 'sm'
          });
      if (proceed) {
        this._downloadFile(`api/SmartGrabber/${this.datasourceId}/datasets/${this.dataset}/tables/${this.table}/export-query-result`,
            postData, () => {
              this.isBusy = false;
            });
        // this._downloadFile(`api/bigQuery/datasets/${this.dataset}/export-query-result`, postData, () => {
        //   this.isBusy = false;
        // });
      } else {
        this.isBusy = false;
      }

      // const response = await axios.post('api/bigQuery/export-query-result', postData);
    },

    async validateQueryModel() {
      if (this.queryModel.columns.length === 0) {
        await this.$bvModal.msgBoxOk('Must select at least 1 column', {centered: true, size: 'sm'});
        return false;
      }
      return true;
    },

    async getTemplates() {
      const response = await axios.post(`api/bigQuery/get-template-list`, this.queryModel);
      this.templates = response.data;
    },

    async loadTemplate(template) {
      const postData = {...template, ...{bqQuery: this.queryModel}};
      // const postData = {bqQuery: this.queryModel, templateName: template.templateName}
      this.currentTemplate = postData;
      const response = await axios.post(`api/bigQuery/get-template/${template.id}`, postData);
      const queryModel = JSON.parse(response.data.bqTemplateObject ?? this.queryModel);
      this.selectedColumns = [...queryModel.columns];
      this.$refs.filterHandler.filters = [];
      _.each(queryModel.filters, (f) => {
        this.$refs.filterHandler.addNewFilter({
          columnName: f.field.name, selectedValues: f.selectedValues
        })
      })
    },

    async saveTemplate({templateName, callback}) {
      if (!(await this.validateQueryModel())) {
        return;
      }
      this.isBusy = true;
      const postData = {
        bqQuery: this.queryModel,
        templateName: templateName
      }
      try {
        await axios.post(`api/bigQuery/add-template`, postData);
        await this.getTemplates();
        callback();
      } finally {
        this.isBusy = false;
      }
    },

    async editTemplate({id, templateName, callback}) {
      if (!(await this.validateQueryModel())) {
        return;
      }
      this.isBusy = true;
      const postData = {
        bqQuery: this.queryModel,
        templateName: templateName
      }
      try {
        await axios.post(`api/bigQuery/edit-template/${id}`, postData);
        await this.getTemplates();
        callback();
      } finally {
        this.isBusy = false;
      }
    },

    async removeTemplate() {
      this.isBusy = true;
      const postData = {
        ...this.currentTemplate
      }
      try {
        await axios.post(`api/bigQuery/remove-template`, postData);
        this.currentTemplate = null;
        await this.getTemplates();
      } finally {
        this.isBusy = false;
      }

    },
  }
}
</script>

<style scoped>
.report-sidebar {
  overflow-y: auto;
  overflow-x: hidden;
  text-overflow: ellipsis;
  height: calc(100vh - 64px);
}

.sidebar-item {
  cursor: pointer;
}

.sidebar-item:hover {
  color: #007bff;
}

.report-body {
  overflow: auto;
  height: calc(100vh - 64px);
}

.table-title {
  font-size: 1.2em;
  font-weight: bold;
}
</style>